/* Display-only composition. Production files and colour analysis never enter this registry. */
(() => {
  'use strict';
  if (window.TodayMerchPreviewEffects) return;
  window.TodayMerchPreviewRenderers ||= Object.create(null);
  const techniques = Object.freeze({
    embroidery: 'embroidery', embroidery_3d: 'embroidery', E: 'embroidery',
    leather_badge: 'patch', printed_badge: 'patch', pu_label_balti_black: 'patch', pu_label_balti_green: 'patch', pu_label_balti_navy: 'patch', 'pu-label': 'patch', 'engraved-patch': 'patch', 'printed-patch-with-embroidered-outline': 'patch',
    doming: 'doming', DO1: 'doming',
    digital_transfer: 'transfer', digital_transfer_os: 'transfer', screen_transfer: 'transfer', screen_transfer_os: 'transfer',
    pad_print: 'print', 'pad-print': 'print', pad_print_drinkware: 'print', digital_print_2: 'print',
    L2: 'engraving', L3: 'engraving', L4: 'engraving', RL: 'engraving',
    engraving: 'engraving', LaserEngraving: 'engraving', laser_engraving: 'engraving',
    laser_engraving_1: 'engraving', laser_engraving_2: 'engraving', laser_engraving_3: 'engraving',
    co2_engraving_2: 'engraving', co2_engraving_3: 'engraving',
  });
  // Reuse the established visual filter on a disposable image. No source File,
  // production mask or quote is created or replaced by this display stage.
  const engravingMetadata = new WeakMap();
  const prepareEngravingCanvas = (source, options) => options.engravingSourcePreparation?.prepared === true
    ? { ...options.engravingSourcePreparation, canvas: copy(source) }
    : window.TodayMerchArtworkEngravingRenderer?.prepareSilhouette(source, { signal: options.signal });
  window.TodayMerchPreviewRenderers.engraving = {
    async render(source, options) {
      const decline = reason => {
        const canvas = copy(source);
        engravingMetadata.set(canvas, { applied: false, reason, previewOnly: true, simulationOnly: true,
          process: 'engraving', mode: 'placement-only', effectiveArtworkUnchanged: true, geometryPreserved: true,
          limitations: ['simulation_only', reason, 'no_fabrication_approval'] });
        return canvas;
      };
      if (options.engravingPreview?.enabled !== true) return copy(source);
      const configuredFinish = ['silver', 'gold', 'tone'].includes(options.engravingPreview.finish);
      const indicative = !configuredFinish && options.engravingPreview.mode === 'indicative-monochrome';
      if (!configuredFinish && !indicative) return decline('engraving_finish_required');
      if (!window.TodayMerchArtworkEngravingRenderer?.prepareSilhouette) return decline('renderer-unavailable');
      const silhouette = prepareEngravingCanvas(source, options);
      if (silhouette.reason) return decline(silhouette.reason);
      const image = source.ownerDocument.createElement('img');
      image.src = silhouette.canvas.toDataURL('image/png');
      await image.decode(); check(options.signal);
      const renderer = window.TodayMerchArtworkEngravingRenderer.create({ document: source.ownerDocument });
      try {
        const rendered = renderer.apply(image, { finish: indicative ? 'neutral' : options.engravingPreview.finish, strength: .55, removeWhite: false });
        const canvas = copy(source), context = canvas.getContext('2d');
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.filter = image.style.getPropertyValue('filter');
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        context.filter = 'none';
        engravingMetadata.set(canvas, { ...rendered.previewMetadata, process: 'engraving',
          silhouette: silhouette.silhouette,
          mode: indicative ? 'indicative-monochrome' : 'configured-finish',
          finishBasis: indicative ? 'illustrative-neutral' : 'configured-finish', effectiveArtworkUnchanged: true,
          limitations: [...rendered.previewMetadata.limitations, ...(indicative ? ['engraving-finish-unknown'] : [])] });
        return canvas;
      } finally { renderer.destroy(); }
    },
    getMetadata: canvas => engravingMetadata.get(canvas),
  };
  function check(signal) { if (signal?.aborted) throw new DOMException('Preview superseded', 'AbortError'); }
  function isCanvas(value) {
    const Canvas = value?.ownerDocument?.defaultView?.HTMLCanvasElement;
    return Boolean(Canvas && value instanceof Canvas);
  }
  function copy(source) {
    if (!isCanvas(source)) throw new TypeError('Preview composition requires a canvas');
    const canvas = source.ownerDocument.createElement('canvas'); canvas.width = source.width; canvas.height = source.height;
    if (canvas.width && canvas.height) canvas.getContext('2d').drawImage(source, 0, 0);
    return canvas;
  }
  // Options describe one job. Keep cancellation live, but detach the mutable
  // geometry, palettes, masks and pixel fields before any renderer can yield.
  function snapshot(value, seen = new WeakMap()) {
    if (!value || typeof value !== 'object' || value instanceof AbortSignal) return value;
    if (seen.has(value)) return seen.get(value);
    if (isCanvas(value)) { const canvas = copy(value); seen.set(value, canvas); return canvas; }
    if (value instanceof ArrayBuffer) return value.slice(0);
    if (ArrayBuffer.isView(value)) return value instanceof DataView
      ? new DataView(value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength)) : value.slice();
    const prototype = Object.getPrototypeOf(value);
    if (!Array.isArray(value) && prototype !== Object.prototype && prototype !== null) return value;
    const result = Array.isArray(value) ? [] : Object.create(null); seen.set(value, result);
    for (const key of Object.keys(value)) result[key] = snapshot(value[key], seen);
    return result;
  }
  function surfaceFinishReason(options) {
    const profile = options.surfaceFinish;
    if (options.qualified === false || options.qualification?.compatible === false
      || (options.qualification?.status && options.qualification.status !== 'allowed')) return 'qualification-rejected';
    if (options.qualified !== true && !(options.qualification?.status === 'allowed' && options.qualification?.compatible === true)) return 'qualification-required';
    if (!profile || profile.qualified !== true || !['foil', 'deboss', 'emboss'].includes(profile.kind)) return 'finish-profile-required';
    if (typeof options.techniqueId !== 'string' || !options.techniqueId.trim() || options.techniqueId !== profile.techniqueId) return 'technique-profile-mismatch';
    if (typeof profile.source !== 'string' || !profile.source.trim() || typeof profile.revision !== 'string' || !profile.revision.trim()) return 'profile-provenance-required';
    if (profile.maskQualified !== true) return 'alpha-mask-required';
    if (typeof profile.color !== 'string' || !/^#[a-f\d]{3}(?:[a-f\d]{3})?$/i.test(profile.color)) return 'finish-color-required';
    return '';
  }
  function textileProfileReason(options) {
    const profile = options.visualProfile;
    if (options.qualified === false || options.qualification?.compatible === false
      || (options.qualification?.status && options.qualification.status !== 'allowed')) return 'qualification-rejected';
    if (!profile || profile.verified !== true || !['dtg', 'screen-print', 'sublimation'].includes(profile.process)) return 'textile-profile-required';
    if (typeof options.techniqueId !== 'string' || !options.techniqueId.trim() || options.techniqueId !== profile.techniqueId) return 'technique-profile-mismatch';
    if (typeof profile.source !== 'string' || !profile.source.trim()) return 'profile-provenance-required';
    if (options.qualification?.status !== 'allowed' || options.qualification?.compatible !== true) return 'qualification-required';
    if (!['textile', 'cotton', 'polyester'].includes(options.material)) return 'textile-material-required';
    return '';
  }
  function conflictingProfiles(options) {
    return options?.surfaceFinish !== undefined && options?.visualProfile !== undefined;
  }
  function effectFor(techniqueId, options) {
    if (conflictingProfiles(options)) return '';
    if (options?.surfaceFinish !== undefined) return !surfaceFinishReason({ ...options, techniqueId }) ? 'surfaceFinish' : '';
    if (options?.visualProfile !== undefined) return !textileProfileReason({ ...options, techniqueId }) ? 'print' : '';
    return Object.prototype.hasOwnProperty.call(techniques, techniqueId) ? techniques[techniqueId] : '';
  }
  function stageInfo(name, applied, reason, metadata) {
    const info = { name, applied, reason, previewOnly: true, simulationOnly: true,
      limitations: Array.isArray(metadata?.limitations) ? metadata.limitations.filter(item => typeof item === 'string') : [] };
    // Forward display descriptors only; renderer output never grants production status.
    for (const key of ['process', 'kind', 'method', 'status', 'mode', 'silhouette', 'colorTreatment', 'profileSource', 'profileRevision',
      'geometryBasis', 'colorBasis', 'finishBasis', 'qualificationBasis']) {
      if (typeof metadata?.[key] === 'string') info[key] = metadata[key];
    }
    for (const key of ['geometryPreserved', 'effectiveArtworkUnchanged', 'simulationReference']) {
      if (typeof metadata?.[key] === 'boolean') info[key] = metadata[key];
    }
    if (typeof metadata?.reason === 'string' && metadata.reason !== reason) info.rendererReason = metadata.reason;
    return info;
  }
  async function render(source, options = {}) {
    options = snapshot(options && typeof options === 'object' ? options : {});
    check(options.signal);
    const applied = [], fallbacks = [], stages = [];
    let canvas = copy(source);
    const width = canvas.width, height = canvas.height;
    const conflict = conflictingProfiles(options);
    const effect = conflict ? '' : options.surfaceFinish !== undefined ? 'surfaceFinish'
      : options.visualProfile !== undefined ? 'print' : effectFor(options.techniqueId);
    const incompatible = options.qualified === false || options.qualification?.compatible === false
      || options.qualification?.status && options.qualification.status !== 'allowed';
    const engravingPreview = effect === 'engraving' && options.engravingPreview?.enabled === true;
    const engravedSupportPreview = effect === 'patch' && options.engravingPreview?.enabled === true;
    let engravedSupportReady = false;
    // The selector binds this display-only permission to an available exact
    // technique. A price lease cannot decide whether an indicative texture is
    // visible. Physical renderer checks still apply to the effective artwork.
    const techniquePreview = ['print', 'embroidery', 'transfer'].includes(effect)
      && options.surfaceFinish === undefined && options.visualProfile === undefined
      && options.techniquePreview?.enabled === true && options.techniquePreview.techniqueId === options.techniqueId
      && options.techniquePreview.basis === 'selected-available-technique';
    const displayPermission = engravingPreview || techniquePreview;
    const curved = options.surface?.kind === 'cylinder' || options.surface?.kind === 'cone';
    const fallback = (name, reason, metadata) => { fallbacks.push(name); stages.push(stageInfo(name, false, reason, metadata)); };
    if (engravedSupportPreview) {
      // Engraved patches and labels keep their own material, outline and colour.
      // Only the imported image background is shared with direct engraving.
      try {
        const cleaned = prepareEngravingCanvas(canvas, options);
        check(options.signal);
        if (!cleaned?.canvas) fallback('artwork-background', cleaned?.reason || 'renderer-unavailable');
        else {
          engravedSupportReady = true;
          if (cleaned.backgroundRemoved || cleaned.frameRemoved) {
            canvas = cleaned.canvas; applied.push('artwork-background');
            stages.push(stageInfo('artwork-background', true, 'automatic-background-removal', {
              previewOnly: true, simulationOnly: true, process: 'artwork-background',
              effectiveArtworkUnchanged: true, geometryPreserved: true, silhouette: cleaned.silhouette,
              limitations: ['simulation_only', 'no_fabrication_approval'],
            }));
          }
        }
      } catch (error) { check(options.signal); fallback('artwork-background', 'preparation-failed'); }
    }
    if (conflict) {
      fallback('surfaceFinish', 'visual-profile-conflict');
      fallback('print', 'visual-profile-conflict');
    }
    if (incompatible && effect && !displayPermission) fallback(effect, 'qualification-rejected');
    if (incompatible && curved && options.geometryOnlyPreview !== true) fallback('curvature', 'qualification-rejected');
    // A known photo surface may be projected before pricing. This explicit mode
    // only warps the original logo; it never grants a manufacturing effect.
    for (const name of [incompatible && !displayPermission ? '' : effect, curved && (!incompatible || options.geometryOnlyPreview === true) ? 'curvature' : ''].filter(Boolean)) {
      check(options.signal);
      if (name === 'patch' && engravedSupportPreview && !engravedSupportReady) { fallback(name, 'engraving-background-unavailable'); continue; }
      const profileReason = name === 'surfaceFinish' ? surfaceFinishReason(options)
        : name === 'print' && options.visualProfile !== undefined ? textileProfileReason(options) : '';
      if (profileReason) { fallback(name, profileReason); continue; }
      if (!window.TodayMerchPreviewRenderers[name] && window.TodayMerchProductModules?.has(options.moduleRoot, 'effect:' + name)) {
        try { await window.TodayMerchProductModules.ensure(options.moduleRoot, 'effect:' + name); }
        catch (_) { check(options.signal); fallback(name, 'renderer-load-failed'); continue; }
        check(options.signal);
      }
      const renderer = window.TodayMerchPreviewRenderers[name];
      if (typeof renderer?.render !== 'function') { fallback(name, 'renderer-unavailable'); continue; }
      try {
        // A third-party renderer cannot accidentally alter the prior stage or the source.
        const rendererOptions = techniquePreview && name === effect
          ? { ...options, qualified: true, qualification: { status: 'allowed', compatible: true } } : options;
        const rendererSource = copy(canvas);
        if (name === 'patch' && engravedSupportPreview) {
          // This temporary alpha mask is tinted by the support's own renderer.
          // A declined support simulation retains the cleaned, coloured logo.
          const context = rendererSource.getContext('2d');
          context.globalCompositeOperation = 'source-in'; context.fillStyle = '#000000';
          context.fillRect(0, 0, width, height);
        }
        const next = await renderer.render(rendererSource, snapshot(rendererOptions));
        check(options.signal);
        if (!isCanvas(next) || next.width !== width || next.height !== height) { fallback(name, 'invalid-preview-output'); continue; }
        let metadata = renderer.getMetadata?.(next) || (name === 'curvature' ? renderer.inspect?.(options) : null);
        if (techniquePreview && name === effect) metadata = { ...metadata,
          qualificationBasis: 'selected-available-technique',
          limitations: [...(metadata?.limitations || []), 'quote-independent-preview', 'artwork-feasibility-unverified'] };
        if (metadata?.applied === false || metadata?.eligible === false || metadata?.mode === 'flat') {
          fallback(name, metadata.reason || 'renderer-declined', metadata); continue;
        }
        // A conservative renderer may return an identical copy when evidence is missing.
        const before = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height).data;
        const after = next.getContext('2d').getImageData(0, 0, next.width, next.height).data;
        const sourceWasCleaned = name === 'engraving' && (options.engravingSourcePreparation?.backgroundRemoved || options.engravingSourcePreparation?.frameRemoved);
        if (!sourceWasCleaned && !after.some((value, index) => value !== before[index])) { fallback(name, 'no-visible-change', metadata); continue; }
        canvas = next; applied.push(name); stages.push(stageInfo(name, true, metadata?.reason || 'simulation', metadata));
      } catch (error) {
        check(options.signal);
        fallback(name, 'renderer-failed');
      }
    }
    return { canvas, applied, fallbacks, stages, previewOnly: true, simulationOnly: true };
  }
  window.TodayMerchPreviewEffects = Object.freeze({ render, copy, effectFor, techniques });
})();
